import { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Eyebrow from '../components/common/Eyebrow';
import EmptyState from '../components/common/EmptyState';
import { CentresShimmer } from '../components/shimmer';
import { useListCentresQuery } from '../store/siteContentApi';

// Build a Google Maps search URL when the admin hasn't set an explicit
// maps_url on the centre. We fold address + city so the pin lands close
// even when the exact coords aren't known.
const mapsLinkFor = (c) => {
  if (c.maps_url) return c.maps_url;
  const q = encodeURIComponent(`${c.name}, ${c.addr}, ${c.city}`);
  return `https://www.google.com/maps/search/?api=1&query=${q}`;
};

// Keep only digits so tel: works on both desktop and mobile browsers.
const telHrefFor = (phone) => {
  const digits = String(phone || '').replace(/[^\d+]/g, '');
  return digits ? `tel:${digits}` : null;
};

// Haversine: great-circle distance between two lat/lng points, in km.
// Accurate to within ~0.5% for distances under a few hundred km — more
// than enough for "how far is this lab from me" UX.
const distanceKm = (lat1, lng1, lat2, lng2) => {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
};

// Format: "1.2 km away" / "850 m away" — prettier than always showing
// decimals when the lab is right next door.
const formatDistance = (km) => {
  if (km == null || Number.isNaN(km)) return null;
  if (km < 1) return `${Math.round(km * 1000)} m away`;
  return `${km.toFixed(1)} km away`;
};

const Centres = () => {
  const { data: centres = [], isLoading, isError } = useListCentresQuery();
  // Read the optional `?city=` deep-link from the URL so home-page
  // city tiles can land directly on the matching filter. Two-way
  // binding: tab clicks also update the URL so the back button and
  // copy-pasted links stay in sync.
  const [searchParams, setSearchParams] = useSearchParams();
  const cityFromUrl = searchParams.get('city') || 'All';

  // Visitor's current geolocation — used to render "X.X km away" on each
  // centre card. We ask once on mount; if the user denies or the browser
  // can't capture it, we surface a small banner with a retry button so
  // the failure mode isn't silent.
  const [userLoc, setUserLoc] = useState(null);
  const [locStatus, setLocStatus] = useState('idle'); // idle | pending | granted | denied | error
  const requestLocation = () => {
    if (!navigator.geolocation) {
      setLocStatus('error');
      return;
    }
    setLocStatus('pending');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocStatus('granted');
      },
      (err) => {
        setLocStatus(err.code === err.PERMISSION_DENIED ? 'denied' : 'error');
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 5 * 60 * 1000 },
    );
  };
  useEffect(() => {
    requestLocation();
  }, []);

  const cities = useMemo(
    () => ['All', ...Array.from(new Set(centres.map((c) => c.city)))],
    [centres]
  );
  const [city, setCity] = useState(cityFromUrl);

  // Adopt URL changes (back button, direct nav, tile click after
  // already on /centres). Falls back to 'All' if the param is dropped.
  useEffect(() => {
    setCity(cityFromUrl);
  }, [cityFromUrl]);

  // If the URL points at a city the dataset doesn't actually have
  // (typo / centre got deactivated), quietly fall back to 'All' once
  // centres are loaded — avoids showing an empty view with a stale
  // selected tab.
  useEffect(() => {
    if (isLoading) return;
    if (cityFromUrl !== 'All' && !cities.includes(cityFromUrl)) {
      setSearchParams({}, { replace: true });
    }
  }, [isLoading, cityFromUrl, cities, setSearchParams]);

  // Tab click → update URL so the filter is shareable / back-able.
  const onPickCity = (next) => {
    if (next === 'All') {
      setSearchParams({}, { replace: false });
    } else {
      setSearchParams({ city: next }, { replace: false });
    }
  };

  const filtered = city === 'All' ? centres : centres.filter((c) => c.city === city);

  return (
    <Box>
      {/* Hero */}
      <Box sx={{ bgcolor: 'var(--forest)', color: '#fff', pt: { xs: 10, md: 13 }, pb: { xs: 5, md: 7 }, px: { xs: 2, md: 7 } }}>
        <Container maxWidth="xl" sx={{ maxWidth: 1300 }}>
          <Eyebrow white>Our Centres</Eyebrow>
          <Typography className="serif" variant="h1" sx={{ color: '#fff', mt: 2, mb: 2 }}>
            Walk in, we're nearby.
          </Typography>
          <Typography sx={{ fontSize: 16, color: 'rgba(255,255,255,0.6)', maxWidth: 540, mb: 4, fontWeight: 300 }}>
            Prefer coming in? Our centres are open 7 AM to 9 PM. Free parking, AC waiting area and no-appointment walk-ins.
          </Typography>
          {cities.length > 1 && (
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              {cities.map((c) => {
                const active = city === c;
                return (
                  <Button
                    key={c}
                    onClick={() => onPickCity(c)}
                    sx={{
                      borderRadius: 100,
                      px: 2.25,
                      py: 0.75,
                      border: '1.5px solid',
                      borderColor: active ? 'var(--sage)' : 'rgba(255,255,255,0.15)',
                      bgcolor: active ? 'var(--sage)' : 'transparent',
                      color: active ? '#fff' : 'rgba(255,255,255,0.7)',
                      fontSize: 13,
                      fontWeight: 700,
                    }}
                  >
                    {c}
                  </Button>
                );
              })}
            </Stack>
          )}
        </Container>
      </Box>

      {/* Grid */}
      <Box sx={{ bgcolor: 'var(--cream)', py: { xs: 5, md: 8 }, px: { xs: 2, md: 7 } }}>
        <Container maxWidth="xl" sx={{ maxWidth: 1300 }}>
          <Typography sx={{ mb: 3, fontWeight: 600, color: 'var(--stone)' }}>
            {isLoading ? 'Loading centres…' : `${filtered.length} centre${filtered.length !== 1 ? 's' : ''}`}
          </Typography>

          {/* Geolocation status — only surfaces when distance ISN'T being
              shown, so users understand why and can grant permission. */}
          {!isLoading && filtered.length > 0 && locStatus !== 'granted' && (
            <Box
              sx={{
                mb: 3, p: 2, borderRadius: '12px',
                bgcolor: '#fff', border: '1px solid var(--border-l)',
                display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
              }}
            >
              <Typography sx={{ fontSize: 13, color: 'var(--charcoal)', flex: 1, minWidth: 200 }}>
                {locStatus === 'pending' && '📍 Getting your location…'}
                {locStatus === 'denied' && '📍 Location blocked. Enable it in your browser to see distance to each lab.'}
                {locStatus === 'error'  && "📍 Couldn't get your location. Try again to see distance to each lab."}
                {locStatus === 'idle'   && '📍 Allow location access to see distance to each lab.'}
              </Typography>
              {locStatus !== 'pending' && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={requestLocation}
                  sx={{ textTransform: 'none', borderRadius: 100, fontWeight: 700 }}
                >
                  {locStatus === 'denied' ? 'Try again' : 'Use my location'}
                </Button>
              )}
            </Box>
          )}

          {isLoading ? (
            <CentresShimmer count={6} />
          ) : isError ? (
            <EmptyState icon="⚠" title="Couldn't load centres" description="Please check your connection and try again." />
          ) : filtered.length === 0 ? (
            <EmptyState
              icon="📍"
              title={city === 'All' ? 'No centres available yet' : 'No centres in this city yet'}
              description="We're expanding fast — check back soon."
            />
          ) : (
            <Grid container spacing={{ xs: 2, md: 2.5 }}>
              {filtered.map((c) => {
                const telHref = telHrefFor(c.phone);
                const dist = userLoc && c.lat != null && c.lng != null
                  ? distanceKm(userLoc.lat, userLoc.lng, c.lat, c.lng)
                  : null;
                const distLabel = formatDistance(dist);
                return (
                  <Grid key={c._id} item xs={12} sm={6} md={4}>
                    <Box
                      sx={{
                        bgcolor: '#fff',
                        border: '1.5px solid var(--border-l)',
                        borderRadius: '20px',
                        overflow: 'hidden',
                        transition: 'all 0.25s',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        '&:hover': {
                          borderColor: 'var(--sage-p)',
                          transform: 'translateY(-4px)',
                          boxShadow: '0 12px 32px rgba(61,122,82,0.1)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          height: 140,
                          background: 'linear-gradient(135deg, var(--mint), var(--sage-l))',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 48,
                        }}
                      >
                        🧪
                      </Box>
                      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', flex: 1 }}>
                        <Typography
                          sx={{
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: '0.14em',
                            textTransform: 'uppercase',
                            color: 'var(--sage)',
                            mb: 0.75,
                          }}
                        >
                          {c.city}
                        </Typography>
                        <Typography className="serif" sx={{ fontSize: 20, fontWeight: 600, mb: 1 }}>
                          {c.name}
                        </Typography>
                        <Stack spacing={0.75} sx={{ mb: 2, flex: 1 }}>
                          <Typography sx={{ fontSize: 13, color: 'var(--charcoal)', fontWeight: 300 }}>
                            📍 {c.addr}, {c.city}
                          </Typography>
                          {c.hours && (
                            <Typography sx={{ fontSize: 13, color: 'var(--charcoal)', fontWeight: 300 }}>
                              🕐 {c.hours}
                            </Typography>
                          )}
                          {c.phone && (
                            <Typography sx={{ fontSize: 13, color: 'var(--charcoal)', fontWeight: 300 }}>
                              📞 {c.phone}
                            </Typography>
                          )}
                          {distLabel && (
                            <Typography sx={{ fontSize: 13, color: 'var(--sage)', fontWeight: 600 }}>
                              📏 {distLabel}
                            </Typography>
                          )}
                        </Stack>
                        <Stack direction="row" spacing={1.25}>
                          <Button
                            variant="outlined"
                            size="small"
                            fullWidth
                            component="a"
                            href={mapsLinkFor(c)}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ color: 'var(--forest)', borderColor: 'var(--border)', textTransform: 'none', fontWeight: 700 }}
                          >
                            Get Directions
                          </Button>
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            fullWidth
                            component="a"
                            href={telHref || undefined}
                            disabled={!telHref}
                            sx={{ textTransform: 'none', fontWeight: 700 }}
                          >
                            Call
                          </Button>
                        </Stack>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Container>
      </Box>
    </Box>
  );
};

export default Centres;
